// Angular
import { CanDeactivateFn, RouterStateSnapshot } from '@angular/router';

// RxJS
import { Observable } from 'rxjs';

/**
 * An interface that marks a components as one that can be used in the canDeactivateGuard.
 */
export interface CanDeactivateComponent {
  /**
   * The function that components must implement with custom navigation-blocking logic.
   *
   * @returns The boolean value indcating whether navigation away from the component
   * is permitted or not.
   */
  canDeactivate: (nextState?: RouterStateSnapshot) => boolean | Promise<boolean> | Observable<boolean>;
}

/**
 * The canDeactivateGuard to attach to any route that requires custom logic for blocking
 * navigation away from the route.
 *
 * @param component Any component that implemnents the CanDeactivateComponent interface.
 *
 * @returns false if the canDeactivate function dictates that navigation should be
 * blocked, otherwise true if navigation is permitted.
 */
export const canDeactivateGuard: CanDeactivateFn<CanDeactivateComponent> = (
  component: CanDeactivateComponent,
  _currentRoute,
  _currentState,
  nextState: RouterStateSnapshot,
) => {
  // Runtime check to ensure the component implements the CanDeactivateComponent interface.
  if (!component || typeof component.canDeactivate !== 'function') {
    console.error('Component does not implement CanDeactivateComponent interface');
    return true; // Return true if the component does not implement the CanDeactivateComponent interface.
  }

  // Return the result of the canDeactivate function if it is implemented.
  return component.canDeactivate(nextState);
};
